/* R796 Battlegrounds page: the store (contract §7.4).
   One synchronous store with fixed, immutable top-level slices. A slice holds
   what the last server reply said and nothing the page worked out itself: no
   balance, odds or reward is ever derived here. set() replaces whole slices
   (patchSlice merges into one for page-local slices such as ui and net).
   Only ui.tab persists, in localStorage bgw-tab. */

export const SLICES = Object.freeze(['session', 'catalog', 'home', 'cards', 'run', 'route', 'net', 'ui']);
export const TAB_IDS = Object.freeze(['home', 'hero', 'party', 'shop']);
/* a tab saved by an older page opens its place now: the R796 names (Battlegrounds 2 menus), Packs (part of the
   Shop since 2026-09-26) and Play (part of Home since 2026-09-27), the same map as the router's ALIAS */
const LEGACY_TAB = Object.freeze({ battle: 'home', events: 'home', play: 'home', levels: 'home', map: 'home', heroes: 'party', upgrades: 'party', champion: 'hero', packs: 'shop' });
const TAB_KEY = 'bgw-tab';

function readTab() {
  try {
    const t = localStorage.getItem(TAB_KEY);
    return TAB_IDS.indexOf(t) >= 0 ? t : (LEGACY_TAB[t] || 'home');
  } catch (_) { return 'home'; }
}
function writeTab(tab) {
  try { localStorage.setItem(TAB_KEY, tab); } catch (_) {}
}

function deepFreeze(value, depth) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value) || depth > 12) return value;
  Object.freeze(value);
  for (const k of Object.keys(value)) deepFreeze(value[k], depth + 1);
  return value;
}

function initialState(initial) {
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  const base = {
    session: { signedIn: false, account: '' },
    catalog: null,
    home: null,
    cards: { byKey: {}, order: [], next: null },
    run: null,
    route: { tab: 'home', view: 'home', path: '#/home', params: {} },
    net: { online: online, pending: 0 },
    ui: { tab: readTab(), fx: null, boot: 'boot', banner: null }
  };
  const out = {};
  for (const name of SLICES) {
    const v = initial && Object.prototype.hasOwnProperty.call(initial, name) ? initial[name] : base[name];
    out[name] = deepFreeze(v, 0);
  }
  return Object.freeze(out);
}

export function createStore(initial) {
  let state = initialState(initial);
  const subs = new Set();

  function set(patch) {
    const p = typeof patch === 'function' ? patch(state) : patch;
    if (!p || typeof p !== 'object') return state;
    const prev = state, changed = [], next = Object.assign({}, state);
    for (const name of Object.keys(p)) {
      if (SLICES.indexOf(name) < 0) throw new Error('unknown_slice:' + name);
      if (p[name] === prev[name]) continue;
      next[name] = deepFreeze(p[name], 0);
      changed.push(name);
    }
    if (!changed.length) return state;
    if (changed.indexOf('ui') >= 0 && next.ui && next.ui.tab !== (prev.ui && prev.ui.tab) && TAB_IDS.indexOf(next.ui.tab) >= 0) writeTab(next.ui.tab);
    state = Object.freeze(next);
    for (const fn of Array.from(subs)) {
      try { fn(state, prev, changed); } catch (err) { setTimeout(() => { throw err; }, 0); }
    }
    return state;
  }

  return Object.freeze({
    get: () => state,
    set: set,
    select: fn => fn(state),
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }
  });
}

/* Merge a partial object into one slice (for page-local slices only). */
export function patchSlice(store, name, partial) {
  const cur = store.get()[name];
  return store.set({ [name]: Object.assign({}, cur || {}, partial) });
}
